import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CreditCard, QrCode, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { CardForm } from './AsaasCheckout';
import { PixCode } from './PixCheckout';
import { PayeeHeader } from './PayeeHeader';
import { CouponField } from './CouponField';
import { maskAs } from '../lib/autoMask';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const errText = (e, f) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : f);
const input = { width: '100%', padding: '0.55rem 0.65rem', borderRadius: 8, border: '1px solid #cbd5e0', fontSize: '0.92rem', marginTop: 3 };
const btn = { background: '#2f855a', color: 'white', border: 'none', borderRadius: 8, padding: '0.7rem', fontWeight: 700, cursor: 'pointer', width: '100%' };
const EMPTY = { holder_name: '', number: '', expiry: '', ccv: '', cpf_cnpj: '', email: '', postal_code: '', address_number: '', phone: '' };

const usePlanPoll = (pid, headers, onPaid) => {
  const [paid, setPaid] = useState(false);
  useEffect(() => {
    if (!pid || paid) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/digital/plan/status/${pid}`, { headers });
        if (data.active) { setPaid(true); clearInterval(t); onPaid?.(); }
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [pid, paid]);
  return paid;
};

const Done = ({ ok, text, onRetry, onDone }) => (
  <div data-testid="plan-result" style={{ textAlign: 'center', padding: '1rem 0' }}>
    {ok ? <CheckCircle2 className="w-10 h-10" style={{ color: '#38a169', margin: '0 auto 0.5rem' }} /> : <AlertCircle className="w-10 h-10" style={{ color: '#d69e2e', margin: '0 auto 0.5rem' }} />}
    <p data-testid="plan-result-message" style={{ fontWeight: 600, color: '#2D3748', marginBottom: '1rem' }}>{text}</p>
    <button type="button" onClick={ok ? onDone : onRetry} data-testid={ok ? 'plan-done-btn' : 'plan-retry-btn'} style={{ ...btn, width: 'auto', padding: '0.6rem 1.4rem' }}>{ok ? 'Concluir' : 'Tentar novamente'}</button>
  </div>
);

// Annual publisher plan: Pix (à vista) or 4x on the card
export const PlanCheckout = ({ token, onPaid, onDone }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [plan, setPlan] = useState(null);
  const [method, setMethod] = useState('pix');
  const [pixPayer, setPixPayer] = useState({ cpf: '', email: '' });
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const [coupon, setCoupon] = useState(null);
  useEffect(() => {
    axios.get(`${API}/digital/plan`, { headers }).then(({ data }) => {
      setPlan(data);
      setPixPayer({ cpf: maskAs('cpfCnpj', data.payer_cpf || ''), email: data.payer_email || '' });
      setForm((s) => ({ ...s, cpf_cnpj: maskAs('cpfCnpj', data.payer_cpf || ''), email: data.payer_email || '', phone: maskAs('phone', data.payer_phone || '') }));
    }).catch(() => setPlan({ price: 3000, installments: 4 }));
  }, [token]);
  const paid = usePlanPoll(view?.pid, headers, onPaid);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { setView({ kind: 'result', ok: false, text: errText(e, 'Não foi possível concluir. Tente novamente.') }); } finally { setBusy(false); }
  };
  const payPix = (e) => { e.preventDefault(); run(async () => {
    const { data } = await axios.post(`${API}/digital/plan/pix`, { ...pixPayer, coupon: coupon?.code }, { headers });
    setView({ kind: 'pix', pix: data, pid: data.payment_id });
  }); };
  const payCard = (d) => run(async () => {
    const { data } = await axios.post(`${API}/digital/plan/card`, { ...d, coupon: coupon?.code }, { headers });
    if (data.active) onPaid?.();
    setView(data.active ? { kind: 'result', ok: true, text: data.message } : { kind: 'wait', pid: data.payment_id, text: data.message });
  });
  if (!plan) return <Loader2 className="w-6 h-6 animate-spin" style={{ margin: '1rem auto', display: 'block' }} />;
  const fullPrice = plan.price || 3000;
  const price = coupon ? coupon.final : fullPrice;
  const n = plan.installments || 4;
  if (paid) return <Done ok text="Pagamento confirmado! Seu plano anual está ativo e todas as funcionalidades foram liberadas." onDone={onDone} />;
  if (view?.kind === 'result') return <Done ok={view.ok} text={view.text} onDone={onDone} onRetry={() => setView(null)} />;
  if (view?.kind === 'wait') return <p data-testid="plan-wait" style={{ textAlign: 'center', padding: '1rem' }}><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{view.text}</p>;
  if (view?.kind === 'pix') return (
    <div data-testid="plan-pix">
      <PixCode pix={view.pix} color="#0d9488" onExpired={() => setView(null)} />
      <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#4a5568', marginTop: 8 }}>Assim que o Pix for pago, o plano é ativado automaticamente.</p>
    </div>
  );
  const tab = (m, Icon, label) => (
    <button type="button" onClick={() => setMethod(m)} data-testid={`plan-tab-${m}`}
      style={{ flex: 1, display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: 6, padding: '0.55rem', borderRadius: 8, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
        border: `1px solid ${method === m ? '#0d9488' : '#e2e8f0'}`, background: method === m ? '#0d948814' : 'white', color: '#2D3748' }}>
      <Icon className="w-4 h-4" />{label}
    </button>
  );
  return (
    <div data-testid="plan-checkout">
      <PayeeHeader />
      <p data-testid="plan-price" style={{ fontWeight: 700, color: '#1a202c', marginBottom: '0.75rem' }}>Plano Editora anual: {coupon && <s style={{ color: '#a0aec0', fontWeight: 400, marginRight: 4 }}>{BRL(fullPrice)}</s>}{BRL(price)} <span style={{ fontWeight: 400, color: '#4a5568' }}>(Pix à vista ou {n}x de {BRL(price / n)} no cartão)</span></p>
      <CouponField checkUrl={`${API}/digital/plan/coupon`} headers={headers} onChange={setCoupon} />
      <div style={{ display: 'flex', gap: 8, marginBottom: '0.9rem' }}>{tab('pix', QrCode, 'Pix')}{tab('card', CreditCard, `Cartão ${n}x`)}</div>
      {method === 'pix' ? (
        <form onSubmit={payPix} data-testid="plan-pix-form" style={{ display: 'grid', gap: '0.6rem' }}>
          <label style={{ fontSize: '0.78rem', color: '#4a5568' }}>CPF/CNPJ do pagador
            <input value={pixPayer.cpf} onChange={(e) => setPixPayer((s) => ({ ...s, cpf: e.target.value }))} data-testid="plan-pix-cpf" inputMode="numeric" required style={input} /></label>
          <label style={{ fontSize: '0.78rem', color: '#4a5568' }}>E-mail
            <input type="email" value={pixPayer.email} onChange={(e) => setPixPayer((s) => ({ ...s, email: e.target.value }))} data-testid="plan-pix-email" required style={input} /></label>
          <button type="submit" disabled={busy} data-testid="plan-pix-btn" style={btn}>{busy ? <Loader2 className="w-4 h-4 animate-spin" style={{ margin: '0 auto' }} /> : `Gerar Pix de ${BRL(price)}`}</button>
        </form>
      ) : <CardForm busy={busy} onPay={payCard} f={form} setF={setForm} label={`Pagar ${n}x de ${BRL(price / n)}`} />}
    </div>
  );
};
